import { BaseResource } from './base';
import {
  WhatsAppConfig,
  WhatsAppMessage,
  CreateWhatsAppConfigDto,
  UpdateWhatsAppConfigDto,
  SendWhatsAppMessageDto,
  SearchWhatsAppMessagesDto,
  ConnectionStatusResponse,
  InitializeConnectionResponse,
  Contact,
  DisconnectResponse,
  SendCatalogMessageDto,
  SendCatalogMessageResponse,
} from '../models/whatsapp';
import { RequestOptions, PaginatedResponseDto } from '../types';

export class WhatsAppResource extends BaseResource {
  private basePath = 'whatsapp';

  /**
   * Get WhatsApp configuration for the current gym
   */
  async getConfig(options?: RequestOptions): Promise<WhatsAppConfig> {
    return this.client.get<WhatsAppConfig>(`${this.basePath}/config`, undefined, options);
  }

  /**
   * Initialize WhatsApp connection
   * Returns QR code if needed
   */
  async initializeConnection(options?: RequestOptions): Promise<InitializeConnectionResponse> {
    return this.client.post<InitializeConnectionResponse>(
      `${this.basePath}/initialize`,
      {},
      options,
    );
  }

  /**
   * Update WhatsApp configuration
   */
  async updateConfig(
    data: UpdateWhatsAppConfigDto,
    options?: RequestOptions,
  ): Promise<WhatsAppConfig> {
    return this.client.put<WhatsAppConfig>(`${this.basePath}/config`, data, options);
  }

  /**
   * Get WhatsApp connection status
   */
  async getConnectionStatus(options?: RequestOptions): Promise<ConnectionStatusResponse> {
    return this.client.get<ConnectionStatusResponse>(
      `${this.basePath}/connection-status`,
      undefined,
      options,
    );
  }

  /**
   * Send a WhatsApp message
   */
  async sendMessage(
    data: SendWhatsAppMessageDto,
    options?: RequestOptions,
  ): Promise<WhatsAppMessage> {
    return this.client.post<WhatsAppMessage>(`${this.basePath}/send-message`, data, options);
  }

  /**
   * Get WhatsApp messages with pagination and filters
   */
  async getMessages(
    params?: SearchWhatsAppMessagesDto,
    options?: RequestOptions,
  ): Promise<PaginatedResponseDto<WhatsAppMessage>> {
    return this.paginate<WhatsAppMessage>(`${this.basePath}/messages`, params, options);
  }

  /**
   * Get a specific WhatsApp message by ID
   */
  async getMessage(id: string, options?: RequestOptions): Promise<WhatsAppMessage> {
    return this.client.get<WhatsAppMessage>(`${this.basePath}/messages/${id}`, undefined, options);
  }

  async listContacts(options?: RequestOptions): Promise<Contact[]> {
    return this.client.get<Contact[]>(`${this.basePath}/contacts`, undefined, options);
  }

  /**
   * Disconnect WhatsApp instance
   * Logs out from WhatsApp without deleting the instance
   */
  async disconnect(options?: RequestOptions): Promise<DisconnectResponse> {
    return this.client.post<DisconnectResponse>(`${this.basePath}/disconnect`, {}, options);
  }

  /**
   * Send catalog message to multiple clients
   */
  async sendCatalogMessage(
    data: SendCatalogMessageDto,
    options?: RequestOptions,
  ): Promise<SendCatalogMessageResponse> {
    return this.client.post<SendCatalogMessageResponse>(
      `${this.basePath}/send-catalog`,
      data,
      options,
    );
  }
}
